// RefinanceQuestions.js

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import calculateRefinancePreQual from "./PreQualRefi";
import LanguageToggle from "./LanguageToggle";
import generatePreQualRefipdf from "./generatePreQualRefipdf";
import uploadAndStorePreQualPDFs from "./uploadAndStorePreQualPDFs";
import {
  frequencyToMonthly,
  formatCurrency,
  capitalizeName,
  formatPhoneNumber,
} from "./formUtils";

const containerStyle = {
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "40px",
  width: "100%",
  maxWidth: "500px",
  margin: "80px auto",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  fontFamily: "Arial, sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxSizing: "border-box",
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  margin: "20px 0",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "16px",
  boxSizing: "border-box",
};

const buttonStyle = {
  backgroundColor: "#0096b2",
  color: "#fff",
  border: "none",
  padding: "14px",
  width: "100%",
  fontWeight: "bold",
  fontSize: "16px",
  marginTop: "10px",
  marginBottom: "10px",
  borderRadius: "8px",
  cursor: "pointer",
};

const QuestionCard = ({ title, children, onContinue, onBack }) => (
  <div className="rfq-container" style={containerStyle}>
    <h2 style={{ textAlign: "center" }}>{title}</h2>
    {children}
    <div style={{ width: "100%" }}>
      {onContinue && (
        <button
          style={{
            ...buttonStyle,
            opacity: onContinue ? 1 : 0.5,
            cursor: onContinue ? "pointer" : "not-allowed",
          }}
          onClick={onContinue}
          disabled={!onContinue}
        >
          Continuar
        </button>
      )}

      {onBack && (
        <button style={buttonStyle} onClick={onBack}>
          Regresar
        </button>
      )}
    </div>
  </div>
);

const RefinanceQuestions = () => {
  const toNum = (v) =>
    parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

  const location = useLocation();
  const navigate = useNavigate();

  const [docId, setDocId] = useState(null);
  const [step, setStep] = useState(1);
  const [formDataRefinance, setFormDataRefinance] = useState({});
  const [history, setHistory] = useState([1]);
  const [selectedDebts, setSelectedDebts] = useState([]);
  const [consolidatedFields, setConsolidatedFields] = useState([]);

  const previousDebtFormData = useRef({});
  const previousSelectedDebts = useRef([]);

  // Dynamically computed docRef
  const docRef = useRef(null);

  const [showAdditionalIncome, setShowAdditionalIncome] = React.useState(
    !!formDataRefinance.additionalWorkIncome || !!formDataRefinance.bonusIncome
  );
  const [showSpouseAdditionalIncome, setShowSpouseAdditionalIncome] = React.useState(
    !!formDataRefinance.spouseAdditionalWorkIncome || !!formDataRefinance.spouseBonusIncome
  );

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || localStorage.getItem("clientDocId");

    if (!id) {
      navigate("/login");
      return;
    }

    setDocId(id);
    localStorage.setItem("clientDocId", id); // ✅ Optional: Store for future access

    const ref = doc(db, "clients", id);
    docRef.current = ref;

    const fetchClientData = async () => {
      try {
        const snapshot = await getDoc(ref);
        if (snapshot.exists()) {
          const data = snapshot.data();
          if (data.formDataRefinance) {
            setFormDataRefinance((prev) => ({
              ...prev,
              ...data.formDataRefinance,
            }));
            if (data.currentStepR && data.currentStepR > 1) {
              setStep(Math.min(data.currentStepR, 4));
              setHistory([1, data.currentStepR]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load client data:", error);
      }
    };

    fetchClientData();
  }, [location]);

  useEffect(() => {
    if (step === 9 && formDataRefinance.maritalStatus !== "Married") {
      handleContinue();
    }
  }, [step, formDataRefinance.maritalStatus]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("Phone") && /[^\d\s()-]/.test(value)) return;
    if (name.includes("Zip") && /[^\d]/.test(value)) return;
    if (name.toLowerCase().includes("name") && /[^a-zA-Z\s]/.test(value))
      return;
    setFormDataRefinance((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name.includes("Phone")) formatted = formatPhoneNumber(value);
    else if (/(income|loan|price|debt|card)/i.test(name))
      formatted = formatCurrency(value);
    else if (/(name|city)/i.test(name)) formatted = capitalizeName(value);
    setFormDataRefinance((prev) => ({ ...prev, [name]: formatted }));
    if (name === "propertyValue") {
      const num = parseFloat(value.toString().replace(/[^0-9.]/g, ""));
      formatted = isNaN(num) ? "" : num.toLocaleString("en-US");
    }
  };

  const getNextStep = (current, data) => {
    if (current === 1 && data.propertyMortgaged === "No") return 3;
    if (current === 1) return 2;
    if (current === 2 && toNum(data.mortgageBalance) > 0) return 3;
    if (current === 3 && formDataRefinance.maritalStatus !== "Married")
      return 5;
    if (current === 4) return 5;
    if (current === 5) {
      return formDataRefinance.employmentType === "Retired" ? 8 : 6;
    }
    if (current === 6 && formDataRefinance.workExperienceDuration === "Yes")
      return formDataRefinance.maritalStatus === "Married" ? 8 : 12;
    if (current === 6 && formDataRefinance.workExperienceDuration === "No")
      return 7;
    if (current === 7)
      return formDataRefinance.maritalStatus === "Married" ? 8 : 12;
    if (current === 8 && formDataRefinance.maritalStatus !== "Married")
      return 12;
    if (current === 8 && formDataRefinance.maritalStatus === "Married")
      return 9;
    if (current === 9) return 10;
    if (
      current === 10 &&
      formDataRefinance.spouseWorkExperienceDuration === "Yes"
    )
      return 12;
    if (
      current === 10 &&
      formDataRefinance.spouseWorkExperienceDuration === "No"
    )
      return 11;
    if (current === 11) return 12;
    return current + 1;
    if (current === 14) return 15;
    if (current === 15) return 16;
  };

  const getPrevStep = (current, data) => {
    if (current === 3 && data.propertyMortgaged === "No") return 1;
    if (current === 12) {
      if (data.maritalStatus !== "Married") {
        if (data.workExperienceDuration === "Yes") return 6;
        else return 7;
      } else {
        if (data.spouseWorkExperienceDuration === "Yes") return 10;
        else return 11;
      }
    }

    if (current === 11) return 10;
    if (current === 10) return 9;
    if (current === 9) return 8;

    if (current === 8) {
      if (data.workExperienceDuration === "Yes") return 6;
      else return 7;
    }

    if (current === 7) return 6;
    if (current === 6) return 5;
    if (current === 5) {
      return data.maritalStatus === "Married" ? 4 : 3;
    }
    if (current === 4) return 3;
    if (current === 3) return 2;
    if (current === 2) return 1;
    return current - 1;
  };

  const handleContinue = async () => {
    if (!canContinue()) return;
    const next = getNextStep(step, formDataRefinance);
    try {
      await updateDoc(docRef.current, {
        formDataRefinance,
        transactionType: "Refinance",
        currentStepR: next,
        updatedAt: serverTimestamp(),
      });
      setStep(next);
      setHistory((prev) => [...prev, next]);
    } catch (err) {
      console.error("Failed to save progress:", err);
    }
  };

  const handleBack = () => {
    const prev = getPrevStep(step, formDataRefinance);
    setStep(prev);
    setHistory((prevHistory) => [...prevHistory.slice(0, -1), prev]);
  };

  const canContinue = () => {
    if (step === 1 && !formDataRefinance.location) return false;
    if (step === 2) {
      if (!formDataRefinance.incomeFrequency) return false;
      if (!formDataRefinance.income) return false;
      if (
        formDataRefinance.incomeFrequency === "Hourly" &&
        (!formDataRefinance.hoursPerWeek ||
          toNum(formDataRefinance.hoursPerWeek) <= 0)
      )
        return false;
    }
    if (step === 3 && !formDataRefinance.maritalStatus) return false;
    if (
      step === 4 &&
      (!formDataRefinance.spouseFirstName || !formDataRefinance.spouseLastName)
    )
      return false;

    if (step === 5 && !formDataRefinance.employmentType) return false;

    if (
      step === 6 &&
      formDataRefinance.employmentType !== "Retired" &&
      !formDataRefinance.workExperienceDuration
    )
      return false;

    if (
      step === 7 &&
      formDataRefinance.employmentType !== "Retired" &&
      !formDataRefinance.sameOccupation2Years
    )
      return false;

    if (
      step === 8 &&
      (!formDataRefinance.spouseIncome ||
        !formDataRefinance.spouseIncomeFrequency ||
        (formDataRefinance.spouseIncomeFrequency === "Hourly" &&
          !formDataRefinance.spouseHoursPerWeek))
    )
      return false;

    if (
      step === 9 &&
      formDataRefinance.maritalStatus === "Married" &&
      !formDataRefinance.spouseEmploymentType
    )
      return false;

    if (step === 10 && !formDataRefinance.spouseWorkExperienceDuration)
      return false;

    if (step === 11 && !formDataRefinance.spouseSameOccupation2Years)
      return false;

    if (step === 12 && !formDataRefinance.hasBankruptcy) return false;

    if (step === 13 && !formDataRefinance.creditScore) return false;

    if (
      step === 13 &&
      formDataRefinance.maritalStatus === "Married" &&
      !formDataRefinance.spouseCreditScore
    )
      return false;

    if (step === 15) {
      if (!formDataRefinance.propertyStreet || !formDataRefinance.propertyZip)
        return false;
      if (
        !formDataRefinance.propertyValue ||
        toNum(formDataRefinance.propertyValue) <= 0
      )
        return false;
      if (!formDataRefinance.propertyType) return false;
    }
    return true;
  };

  const getControls = () => ({
    onContinue: canContinue() ? handleContinue : null,
    onBack: step > 1 ? handleBack : null,
  });

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <QuestionCard
            title="¿En qué municipio está ubicada su residencia?"
            {...getControls()}
          >
            <select
              name="location"
              value={formDataRefinance.location || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione Municipio --</option>
              {[
                "Adjuntas",
                "Aguada",
                "Aguadilla",
                "Aguas Buenas",
                "Aibonito",
                "Añasco",
                "Arecibo",
                "Arroyo",
                "Barceloneta",
                "Barranquitas",
                "Bayamón",
                "Cabo Rojo",
                "Caguas",
                "Camuy",
                "Canóvanas",
                "Carolina",
                "Cataño",
                "Cayey",
                "Ceiba",
                "Ciales",
                "Cidra",
                "Coamo",
                "Comerío",
                "Corozal",
                "Culebra",
                "Dorado",
                "Fajardo",
                "Florida",
                "Guánica",
                "Guayama",
                "Guayanilla",
                "Guaynabo",
                "Gurabo",
                "Hatillo",
                "Hormigueros",
                "Humacao",
                "Isabela",
                "Jayuya",
                "Juana Díaz",
                "Juncos",
                "Lajas",
                "Lares",
                "Las Marías",
                "Las Piedras",
                "Loíza",
                "Luquillo",
                "Manatí",
                "Maricao",
                "Maunabo",
                "Mayagüez",
                "Moca",
                "Morovis",
                "Naguabo",
                "Naranjito",
                "Orocovis",
                "Patillas",
                "Peñuelas",
                "Ponce",
                "Quebradillas",
                "Rincón",
                "Río Grande",
                "Sabana Grande",
                "Salinas",
                "San Germán",
                "San Juan",
                "San Lorenzo",
                "San Sebastián",
                "Santa Isabel",
                "Toa Alta",
                "Toa Baja",
                "Trujillo Alto",
                "Utuado",
                "Vega Alta",
                "Vega Baja",
                "Vieques",
                "Villalba",
                "Yabucoa",
                "Yauco",
              ].map((municipio) => (
                <option key={municipio} value={municipio}>
                  {municipio}
                </option>
              ))}
            </select>
            <button
              onClick={() => navigate("/es/select-transaction")}
              style={{
                marginTop: "1.5rem",
                width: "100%",
                padding: "0.75rem",
                backgroundColor: "#0098BA",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#3AB09E";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#0098BA";
              }}
            >
              ⬅ Regresar
            </button>
          </QuestionCard>
        );

        case 2: {
          const calculatedMonthlyIncome = Number(
            frequencyToMonthly(
              toNum(formDataRefinance.income),
              formDataRefinance.incomeFrequency,
              toNum(formDataRefinance.hoursPerWeek || 40)
            ).toFixed(2)
          );
        
          const additionalWorkMonthly = frequencyToMonthly(
            toNum(formDataRefinance.additionalWorkIncome),
            formDataRefinance.additionalWorkIncomeFrequency,
            toNum(formDataRefinance.additionalWorkHoursPerWeek || 40)
          );
          const bonusMonthly = frequencyToMonthly(
            toNum(formDataRefinance.bonusIncome),
            formDataRefinance.bonusIncomeFrequency
          );
        
          return (
            <QuestionCard title="¿Cuál es su ingreso actual?" {...getControls()}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "#0096b2",
                  marginBottom: "10px",
                }}
              >
                Ingreso mensual estimado: $
                {Number(calculatedMonthlyIncome).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
        
              <select
                name="incomeFrequency"
                value={formDataRefinance.incomeFrequency || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  maxHeight: "150px",
                  overflowY: "auto",
                  appearance: "none",
                }}
              >
                <option value="">-- Seleccione Frecuencia --</option>
                <option value="Hourly">Por Hora</option>
                <option value="Weekly">Semanal</option>
                <option value="Bi-Weekly">Bi-Semanal</option>
                <option value="Semi-Monthly">Quincenal</option>
                <option value="Monthly">Mensual</option>
                <option value="Annually">Anual</option>
              </select>
              <input
                className="rfq-input"
                name="income"
                value={formDataRefinance.income || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ingreso"
                style={inputStyle}
              />
        
              {formDataRefinance.incomeFrequency === "Hourly" && (
                <input
                  className="rfq-input"
                  name="hoursPerWeek"
                  value={formDataRefinance.hoursPerWeek || ""}
                  onChange={handleChange}
                  placeholder="Horas por semana"
                  style={inputStyle}
                />
              )}
        
              {/* --- Botón y campos de ingreso adicional --- */}
              <div style={{ marginTop: "18px" }}>
                {!showAdditionalIncome ? (
                  <button
                    type="button"
                    style={{
                      padding: "7px 18px",
                      borderRadius: "7px",
                      background: "#278bc4",
                      color: "#fff",
                      fontWeight: 600,
                      border: "none",
                      fontSize: "1em",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowAdditionalIncome(true)}
                  >
                    Agregar ingreso adicional
                  </button>
                ) : (
                  <>
                    <div style={{ marginTop: "14px", marginBottom: "8px" }}>
                      <b>Otro ingreso de trabajo</b>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                      <input
                        type="number"
                        min="0"
                        name="additionalWorkIncome"
                        placeholder="Cantidad"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataRefinance.additionalWorkIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="additionalWorkIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataRefinance.additionalWorkIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Frecuencia</option>
                        <option value="Hourly">Por Hora</option>
                        <option value="Weekly">Semanal</option>
                        <option value="Monthly">Mensual</option>
                        <option value="Annually">Anual</option>
                      </select>
                      {formDataRefinance.additionalWorkIncomeFrequency === "Hourly" && (
                        <input
                          type="number"
                          min="0"
                          name="additionalWorkHoursPerWeek"
                          placeholder="Horas por semana"
                          style={{ width: "100px", borderRadius: 6, padding: "7px" }}
                          value={formDataRefinance.additionalWorkHoursPerWeek || ""}
                          onChange={handleChange}
                        />
                      )}
                    </div>
                    {additionalWorkMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 8 }}>
                        <small>
                          <strong>Equivalente mensual: </strong>$
                          {Number(additionalWorkMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                      <b>Bono</b>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        name="bonusIncome"
                        placeholder="Cantidad"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataRefinance.bonusIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="bonusIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataRefinance.bonusIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Frecuencia</option>
                        <option value="Monthly">Mensual</option>
                        <option value="Annually">Anual</option>
                      </select>
                    </div>
                    {bonusMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 10 }}>
                        <small>
                          <strong>Equivalente mensual: </strong>$
                          {Number(bonusMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        marginTop: "15px",
                        color: "#ff4b00",
                        background: "none",
                        border: "none",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setShowAdditionalIncome(false);
                        handleChange({ target: { name: "additionalWorkIncome", value: "" } });
                        handleChange({ target: { name: "additionalWorkIncomeFrequency", value: "" } });
                        handleChange({ target: { name: "additionalWorkHoursPerWeek", value: "" } });
                        handleChange({ target: { name: "bonusIncome", value: "" } });
                        handleChange({ target: { name: "bonusIncomeFrequency", value: "" } });
                      }}
                    >
                      Quitar ingreso adicional
                    </button>
                  </>
                )}
              </div>
            </QuestionCard>
          );
        }

      case 3:
        return (
          <QuestionCard title="¿Cuál es su estado civil?" {...getControls()}>
            <select
              name="maritalStatus"
              value={formDataRefinance.maritalStatus || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="Married">Casado(a)</option>
              <option value="Unmarried">No Casado(a)</option>
            </select>
          </QuestionCard>
        );

      case 4:
        return (
          <QuestionCard title="Información del cónyuge" {...getControls()}>
            <input
              className="rfq-input"
              name="spouseFirstName"
              value={formDataRefinance.spouseFirstName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Nombre"
              style={inputStyle}
            />
            <input
              className="rfq-input"
              name="spouseLastName"
              value={formDataRefinance.spouseLastName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Apellidos"
              style={inputStyle}
            />
            <input
              className="rfq-input"
              name="spousePhone"
              value={formDataRefinance.spousePhone || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Teléfono"
              style={inputStyle}
            />
            <input
              className="rfq-input"
              name="spouseEmail"
              value={formDataRefinance.spouseEmail || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Correo electrónico"
              style={inputStyle}
            />
          </QuestionCard>
        );

      case 5:
        return (
          <QuestionCard
            title="¿Cuál es su tipo de empleo actual?"
            {...getControls()}
          >
            <select
              name="employmentType"
              value={formDataRefinance.employmentType || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Tipo de empleo --</option>
              <option value="Salaried">Empleado (W-2)</option>
              <option value="Contractor480">
                Servicios Profesionales (480)
              </option>
              <option value="Self-Employed">Negocio Propio</option>
              <option value="Retired">Retirado(a)</option>
            </select>
          </QuestionCard>
        );

      case 6:
        return (
          <QuestionCard
            title="¿Lleva 2 años en su empleo actual?"
            {...getControls()}
          >
            <select
              name="workExperienceDuration"
              value={formDataRefinance.workExperienceDuration || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="Yes">Sí</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );

      case 7:
        return (
          <QuestionCard
            title="¿Ha trabajado en la misma ocupación durante los últimos 2 años?"
            {...getControls()}
          >
            <select
              name="sameOccupation2Years"
              value={formDataRefinance.sameOccupation2Years || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="Yes">Sí</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );

        case 8: {
          const spouseMonthlyIncome =
            formDataRefinance.maritalStatus === "Married" &&
            formDataRefinance.prenuptialAgreement !== "Yes"
              ? frequencyToMonthly(
                  toNum(formDataRefinance.spouseIncome),
                  formDataRefinance.spouseIncomeFrequency,
                  toNum(formDataRefinance.spouseHoursPerWeek || 40)
                )
              : 0;
        
          const spouseAdditionalWorkMonthly = frequencyToMonthly(
            toNum(formDataRefinance.spouseAdditionalWorkIncome),
            formDataRefinance.spouseAdditionalWorkIncomeFrequency,
            toNum(formDataRefinance.spouseAdditionalWorkHoursPerWeek || 40)
          );
          const spouseBonusMonthly = frequencyToMonthly(
            toNum(formDataRefinance.spouseBonusIncome),
            formDataRefinance.spouseBonusIncomeFrequency
          );
        
          return (
            <QuestionCard
              title="¿Cuál es el ingreso actual de su cónyuge?"
              {...getControls()}
            >
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "#0096b2",
                  marginBottom: "10px",
                }}
              >
                Ingreso mensual estimado: $
                {toNum(spouseMonthlyIncome).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
        
              <input
                className="rfq-input"
                name="spouseIncome"
                value={formDataRefinance.spouseIncome || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ingreso"
                style={inputStyle}
              />
              <select
                name="spouseIncomeFrequency"
                value={formDataRefinance.spouseIncomeFrequency || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "16px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  maxHeight: "150px",
                  overflowY: "auto",
                  appearance: "none",
                }}
              >
                <option value="">-- Seleccione Frecuencia --</option>
                <option value="Hourly">Por Hora</option>
                <option value="Weekly">Semanal</option>
                <option value="Bi-Weekly">Bi-Semanal</option>
                <option value="Semi-Monthly">Quincenal</option>
                <option value="Monthly">Mensual</option>
                <option value="Annually">Anual</option>
              </select>
              {formDataRefinance.spouseIncomeFrequency === "Hourly" && (
                <input
                  className="rfq-input"
                  name="spouseHoursPerWeek"
                  value={formDataRefinance.spouseHoursPerWeek || ""}
                  onChange={handleChange}
                  placeholder="Horas por semana"
                  style={inputStyle}
                />
              )}
        
              {/* --- Botón y campos de ingreso adicional del cónyuge --- */}
              <div style={{ marginTop: "18px" }}>
                {!showSpouseAdditionalIncome ? (
                  <button
                    type="button"
                    style={{
                      padding: "7px 18px",
                      borderRadius: "7px",
                      background: "#278bc4",
                      color: "#fff",
                      fontWeight: 600,
                      border: "none",
                      fontSize: "1em",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowSpouseAdditionalIncome(true)}
                  >
                    Agregar ingreso adicional
                  </button>
                ) : (
                  <>
                    <div style={{ marginTop: "14px", marginBottom: "8px" }}>
                      <b>Otro ingreso de trabajo (cónyuge)</b>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                      <input
                        type="number"
                        min="0"
                        name="spouseAdditionalWorkIncome"
                        placeholder="Cantidad"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataRefinance.spouseAdditionalWorkIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="spouseAdditionalWorkIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataRefinance.spouseAdditionalWorkIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Frecuencia</option>
                        <option value="Hourly">Por Hora</option>
                        <option value="Weekly">Semanal</option>
                        <option value="Monthly">Mensual</option>
                        <option value="Annually">Anual</option>
                      </select>
                      {formDataRefinance.spouseAdditionalWorkIncomeFrequency === "Hourly" && (
                        <input
                          type="number"
                          min="0"
                          name="spouseAdditionalWorkHoursPerWeek"
                          placeholder="Horas por semana"
                          style={{ width: "100px", borderRadius: 6, padding: "7px" }}
                          value={formDataRefinance.spouseAdditionalWorkHoursPerWeek || ""}
                          onChange={handleChange}
                        />
                      )}
                    </div>
                    {spouseAdditionalWorkMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 8 }}>
                        <small>
                          <strong>Equivalente mensual: </strong>$
                          {Number(spouseAdditionalWorkMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                      <b>Bono (cónyuge)</b>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        name="spouseBonusIncome"
                        placeholder="Cantidad"
                        style={{ width: "120px", borderRadius: 6, padding: "7px" }}
                        value={formDataRefinance.spouseBonusIncome || ""}
                        onChange={handleChange}
                      />
                      <select
                        name="spouseBonusIncomeFrequency"
                        style={{ borderRadius: 6, padding: "7px", width: "140px" }}
                        value={formDataRefinance.spouseBonusIncomeFrequency || ""}
                        onChange={handleChange}
                      >
                        <option value="">Frecuencia</option>
                        <option value="Monthly">Mensual</option>
                        <option value="Annually">Anual</option>
                      </select>
                    </div>
                    {spouseBonusMonthly ? (
                      <div style={{ marginLeft: 2, marginBottom: 10 }}>
                        <small>
                          <strong>Equivalente mensual: </strong>$
                          {Number(spouseBonusMonthly).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </small>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        marginTop: "15px",
                        color: "#ff4b00",
                        background: "none",
                        border: "none",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setShowSpouseAdditionalIncome(false);
                        handleChange({ target: { name: "spouseAdditionalWorkIncome", value: "" } });
                        handleChange({ target: { name: "spouseAdditionalWorkIncomeFrequency", value: "" } });
                        handleChange({ target: { name: "spouseAdditionalWorkHoursPerWeek", value: "" } });
                        handleChange({ target: { name: "spouseBonusIncome", value: "" } });
                        handleChange({ target: { name: "spouseBonusIncomeFrequency", value: "" } });
                      }}
                    >
                      Quitar ingreso adicional
                    </button>
                  </>
                )}
              </div>
            </QuestionCard>
          );
        }

      case 9:
        return (
          <QuestionCard
            title="¿Cuál es el tipo de empleo actual de su cónyuge?"
            {...getControls()}
          >
            <select
              name="spouseEmploymentType"
              value={formDataRefinance.spouseEmploymentType || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione tipo de empleo --</option>
              <option value="Salaried">Empleado (W-2)</option>
              <option value="Contractor480">
                Servicios Profesionales (480)
              </option>
              <option value="Self-Employed">Negocio Propio</option>
              <option value="Retired">Retirado(a)</option>
            </select>
          </QuestionCard>
        );

      case 10:
        return (
          <QuestionCard
            title="¿Su cónyuge lleva 2 años en su empleo actual?"
            {...getControls()}
          >
            <select
              name="spouseWorkExperienceDuration"
              value={formDataRefinance.spouseWorkExperienceDuration || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="Yes">Sí</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );

      case 11:
        return (
          <QuestionCard
            title="¿Su cónyuge ha trabajado en la misma ocupación durante los últimos 2 años?"
            {...getControls()}
          >
            <select
              name="spouseSameOccupation2Years"
              value={formDataRefinance.spouseSameOccupation2Years || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="Yes">Sí</option>
              <option value="No">No</option>
            </select>
          </QuestionCard>
        );

      case 12:
        return (
          <QuestionCard
            title="¿Ha tenido quiebras, ejecución hipotecaria o cuentas en perdida en los últimos 7 años?"
            {...getControls()}
          >
            <select
              name="hasBankruptcy"
              value={formDataRefinance.hasBankruptcy || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                maxHeight: "150px",
                overflowY: "auto",
                appearance: "none",
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="No">No</option>
              <option value="Yes">Sí</option>
            </select>
          </QuestionCard>
        );

      case 13:
        return (
          <QuestionCard
            title="¿Cuál es su puntuación de crédito actual?"
            {...getControls()}
          >
            <select
              name="creditScore"
              value={formDataRefinance.creditScore || ""}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">-- Seleccione el rango --</option>
              <option value="Below 580">Below 580</option>
              <option value="580–619">580–619</option>
              <option value="620–679">620–679</option>
              <option value="680–739">680–739</option>
              <option value="740–749">740–749</option>
              <option value="750+">750 or higher</option>
            </select>

            {formDataRefinance.maritalStatus === "Married" && (
              <>
                <div style={{ marginTop: "20px", fontWeight: "bold" }}>
                  ¿Cuál es la puntuación de crédito actual de su cónyuge?
                </div>
                <select
                  name="spouseCreditScore"
                  value={formDataRefinance.spouseCreditScore || ""}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">-- Seleccione el rango --</option>
                  <option value="Below 580">Below 580</option>
                  <option value="580–619">580–619</option>
                  <option value="620–679">620–679</option>
                  <option value="680–739">680–739</option>
                  <option value="740–749">740–749</option>
                  <option value="750+">750 or higher</option>
                </select>
              </>
            )}
          </QuestionCard>
        );

      case 14:
        const debtFieldMap = {
          mortgageLoan: ["mortgageBalance"],
          autoLoan: [
            "AutoLoanBalance",
            "AutoLoanMonthly",
            "spouseAutoLoanBalance",
            "spouseAutoLoanMonthly",
          ],
          personalLoan: [
            "personalLoanBalance",
            "personalLoanMonthly",
            "spousePersonalLoanBalance",
            "spousePersonalLoanMonthly",
          ],
          studentLoan: [
            "studentLoanBalance",
            "studentLoanMonthly",
            "spouseStudentLoanBalance",
            "spouseStudentLoanMonthly",
          ],
          creditCards: [
            "creditCardBalance",
            "CreditCardMonthly",
            "creditCard2Balance",
            "CreditCard2Monthly",
          ],
          storeCards: [
            "storeCardsBalance",
            "storeCardsMonthly",
            "spouseStoreCardsBalance",
            "spouseStoreCardsMonthly",
          ],
          furnitureLoans: [
            "furnitureLoansBalance",
            "furnitureLoansMonthly",
            "spouseFurnitureLoansBalance",
            "spouseFurnitureLoansMonthly",
          ],
          otherDebts: [
            "otherDebtsBalance",
            "otherDebtsMonthly",
            "spouseOtherDebtsBalance",
            "spouseOtherDebtsMonthly",
          ],
        };

        const debtGroups = Object.keys(debtFieldMap);
        const allFieldNames = Object.values(debtFieldMap).flat();
        const allBalanceFields = allFieldNames.filter((name) =>
          name.toLowerCase().includes("balance")
        );
        const allMonthlyFields = allFieldNames.filter((name) =>
          name.toLowerCase().includes("monthly")
        );

        const monthlyToBalanceMap = {
          AutoLoanMonthly: "AutoLoanBalance",
          spouseAutoLoanMonthly: "spouseAutoLoanBalance",
          personalLoanMonthly: "personalLoanBalance",
          spousePersonalLoanMonthly: "spousePersonalLoanBalance",
          studentLoanMonthly: "studentLoanBalance",
          spouseStudentLoanMonthly: "spouseStudentLoanBalance",
          CreditCardMonthly: "creditCardBalance",
          CreditCard2Monthly: "creditCard2Balance",
          storeCardsMonthly: "storeCardsBalance",
          spouseStoreCardsMonthly: "spouseStoreCardsBalance",
          furnitureLoansMonthly: "furnitureLoansBalance",
          spouseFurnitureLoansMonthly: "spouseFurnitureLoansBalance",
          otherDebtsMonthly: "otherDebtsBalance",
          spouseOtherDebtsMonthly: "spouseOtherDebtsBalance",
        };

        const consolidatedBalanceSet = new Set(
          (consolidatedFields || []).map((field) => field.toLowerCase())
        );

        const consolidatedMonthlyFields = Object.entries(monthlyToBalanceMap)
          .filter(
            ([monthly, balance]) =>
              consolidatedBalanceSet.has(balance.toLowerCase()) ||
              balance === "mortgageBalance"
          )
          .map(([monthly]) => monthly);

        const consolidatedBalanceTotal = allBalanceFields.reduce(
          (sum, field) => {
            const lowerField = field.toLowerCase();
            if (
              consolidatedBalanceSet.has(lowerField) ||
              field === "mortgageBalance"
            ) {
              sum += toNum((formDataRefinance || {})[field]);
            }
            return sum;
          },
          0
        );

        const monthlyPaymentRemovedTotal = consolidatedMonthlyFields.reduce(
          (sum, field) => {
            sum += toNum((formDataRefinance || {})[field]);
            return sum;
          },
          0
        );

        // --- Monthly Payments Still Owed ---
        const allMonthlyPayments = [
          "AutoLoanMonthly",
          "spouseAutoLoanMonthly",
          "personalLoanMonthly",
          "spousePersonalLoanMonthly",
          "studentLoanMonthly",
          "spouseStudentLoanMonthly",
          "CreditCardMonthly",
          "CreditCard2Monthly",
          "storeCardsMonthly",
          "spouseStoreCardsMonthly",
          "furnitureLoansMonthly",
          "spouseFurnitureLoansMonthly",
          "otherDebtsMonthly",
          "spouseOtherDebtsMonthly",
        ];

        const monthlyPaymentRemainingTotal = allMonthlyPayments.reduce(
          (sum, field) => {
            const relatedBalance = monthlyToBalanceMap[field];
            if (
              relatedBalance &&
              !consolidatedBalanceSet.has(relatedBalance.toLowerCase()) &&
              relatedBalance !== "mortgageBalance"
            ) {
              sum += toNum((formDataRefinance || {})[field]);
            }
            return sum;
          },
          0
        );

        return (
          <QuestionCard
            title="¿Cuáles deudas desea consolidar?"
            {...getControls()}
          >
            {/* Checkbox section with circular buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", width: "85%" }}>
                <div style={{ width: "50%" }}>
                  {debtGroups.slice(0, 4).map((item) => (
                    <label
                      key={item}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "10px",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDebts.includes(item)}
                        onChange={() => {
                          setSelectedDebts((prev) =>
                            prev.includes(item)
                              ? prev.filter((v) => v !== item)
                              : [...prev, item]
                          );
                        }}
                      />
                      {item === "autoLoan"
                        ? "Auto Loans"
                        : item === "mortgageLoan"
                        ? "Mortgage Loan"
                        : item
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (c) => c.toUpperCase())}
                    </label>
                  ))}
                </div>
                <div style={{ width: "50%" }}>
                  {debtGroups.slice(4).map((item) => (
                    <label
                      key={item}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "10px",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDebts.includes(item)}
                        onChange={() => {
                          setSelectedDebts((prev) =>
                            prev.includes(item)
                              ? prev.filter((v) => v !== item)
                              : [...prev, item]
                          );
                        }}
                      />
                      {item
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (c) => c.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  alignItems: "center",
                  paddingTop: "10px",
                }}
              >
                <div
                  onClick={() => {
                    const allSelected = debtGroups.every((item) =>
                      selectedDebts.includes(item)
                    );
                    setSelectedDebts(allSelected ? [] : [...debtGroups]);
                  }}
                  title="Select All"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: "#0096b2",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  All
                </div>

                <div
                  onClick={() => {
                    previousDebtFormData.current = { ...formData };
                    const cleared = { ...formDataRefinance };
                    allFieldNames.forEach((f) => (cleared[f] = ""));
                    setFormDataRefinance(cleared); // ✅ Only clears inputs
                  }}
                  title="Clear"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: "#d9534f",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  🗑
                </div>

                <div
                  onClick={() => {
                    setFormDataRefinance(previousDebtFormData.current); // ✅ Only restores inputs
                  }}
                  title="Undo"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: "#5bc0de",
                    color: "black",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "18px",
                  }}
                >
                  ↩
                </div>
              </div>
            </div>

            {/* Input grid: 2 top, 2 bottom side by side */}
            {selectedDebts.map((debt) => {
              const fields = Array.from(new Set(debtFieldMap[debt] || [])); // Deduplicate fields
              const hasBalance = fields.some((f) =>
                f.toLowerCase().includes("balance")
              );
              const hasMonthly = fields.some((f) =>
                f.toLowerCase().includes("monthly")
              );

              return (
                <div key={debt}>
                  {(hasBalance || hasMonthly) && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                        marginBottom: "5px",
                        fontWeight: "bold",
                        fontSize: "16px",
                      }}
                    >
                      <div>Balance</div>
                      <div>Pago mensual</div>
                    </div>
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginBottom: "15px",
                    }}
                  >
                    {fields.map((field) => {
                      const isBalanceField = field
                        .toLowerCase()
                        .includes("balance");
                      const skipCheckbox = field === "mortgageBalance";

                      const isChecked = consolidatedFields.includes(field);

                      return (
                        <div
                          key={field}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {!skipCheckbox && isBalanceField && (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                setConsolidatedFields((prev) => {
                                  const updated = e.target.checked
                                    ? [...prev, field]
                                    : prev.filter((f) => f !== field);
                                  return Array.from(new Set(updated));
                                });
                              }}
                              title="Consolidate this balance"
                            />
                          )}
                          <input
                            name={field}
                            value={formDataRefinance[field] || ""}
                            onChange={handleChange}
                            onBlur={(e) => {
                              const raw = toNum(e.target.value);
                              const formatted = raw.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                                minimumFractionDigits: 2,
                              });
                              setFormDataRefinance((prev) => ({
                                ...prev,
                                [field]: formatted,
                              }));
                            }}
                            placeholder={field
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (c) => c.toUpperCase())}
                            style={{ ...inputStyle, width: "100%" }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div
              style={{
                marginTop: "30px",
                paddingTop: "20px",
                borderTop: "1px solid #ccc",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: "18px" }}>
                Resumen de Consolidación:
              </div>
              <div>
                Total Consolidado: ${consolidatedBalanceTotal.toFixed(2)}
              </div>
              <div>
                Pagos Mensuales Eliminados: $
                {monthlyPaymentRemovedTotal.toFixed(2)}
              </div>
              <div>
                Pagos Mensuales que Seguirá Pagando: $
                {monthlyPaymentRemainingTotal.toFixed(2)}
              </div>
            </div>
          </QuestionCard>
        );

      case 15:
        return (
          <QuestionCard
            title="Información de la propiedad a refinanciar"
            {...getControls()}
          >
            <div style={{ marginBottom: "20px", width: "100%" }}>
              <h4>Dirección residencial</h4>
              <input
                className="rfq-input"
                name="propertyStreet"
                value={formDataRefinance.propertyStreet || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Dirección"
                style={{
                  ...inputStyle,
                  width: "100%",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <h4>ZIP Code</h4>
                <input
                  className="rfq-input"
                  name="propertyZip"
                  value={formDataRefinance.propertyZip || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="ZIP Code"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <h4>Estado</h4>
                <input
                  className="rfq-input"
                  name="propertyState"
                  value="Puerto Rico"
                  readOnly
                  style={{
                    ...inputStyle,
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <h4>Municipio</h4>
                <input
                  className="rfq-input"
                  name="location"
                  value={formDataRefinance.location || ""}
                  readOnly
                  style={{
                    ...inputStyle,
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    cursor: "not-allowed",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <h4>Valor estimado</h4>
                <input
                  className="rfq-input"
                  name="propertyValue"
                  value={formDataRefinance.propertyValue || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Valor estimado"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: "10px", width: "100%" }}>
              <h4>Tipo de propiedad</h4>
              <select
                name="propertyType"
                value={formDataRefinance.propertyType || ""}
                onChange={handleChange}
                style={{
                  ...inputStyle,
                  width: "100%",
                  appearance: "none",
                }}
              >
                <option value="">-- Seleccione tipo de propiedad --</option>
                <option value="Single Family">Casa</option>
                <option value="Multi-Family">Casa Multifamiliar</option>
                <option value="Condo">Condominio</option>
              </select>
            </div>

            {formDataRefinance.propertyType === "Condo" && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "12px",
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  border: "1px solid #ffeeba",
                  borderRadius: "6px",
                  fontSize: "14px",
                  width: "100%",
                }}
              >
                Nota: Los condominios deben estar aprobados actualmente para
                financiamiento por FHA.
              </div>
            )}
          </QuestionCard>
        );

      case 16:
        const handleRefinanceSubmit = async () => {
          const propertyValue = toNum(formDataRefinance.propertyValue);
          const includesNonMortgageDebts = (consolidatedFields || []).some(
            (field) => field.toLowerCase() !== "mortgagebalance"
          );
          const ltvCap = includesNonMortgageDebts ? 0.8 : 0.965;
          const maxAllowedLoan = propertyValue * ltvCap;
          const consolidatedLoan = toNum(
            formDataRefinance.totalLoanRequested || 0
          );

          if (consolidatedLoan > maxAllowedLoan) {
            alert(
              "La cantidad solicitada para el refinanciamiento excede el máximo permitido según el valor ingresado de la propiedad. Por favor, comuníquese directamente con un empleado de Home Mortgage Bankers. La información de contacto estará disponible en su panel (dashboard)."
            );
            navigate("/es/dashboard");
            return;
          }

          // ✅ Compute total debts here:
          const totalDebts = (
            toNum(formDataRefinance.autoLoan) +
            toNum(formDataRefinance.personalLoan) +
            (toNum(formDataRefinance.studentLoanMonthly) ||
              toNum(formDataRefinance.studentLoanTotal) * 0.005) +
            toNum(formDataRefinance.creditCard1) +
            toNum(formDataRefinance.creditCard2) +
            toNum(formDataRefinance.creditCard3) +
            toNum(formDataRefinance.creditCard4) +
            toNum(formDataRefinance.storeCards) +
            toNum(formDataRefinance.furnitureLoans) +
            toNum(formDataRefinance.applianceLoans) +
            toNum(formDataRefinance.otherDebts) +
            toNum(formDataRefinance.spouseAutoLoan) +
            toNum(formDataRefinance.spousePersonalLoan) +
            (toNum(formDataRefinance.spouseStudentLoanMonthly) ||
              toNum(formDataRefinance.spouseStudentLoanTotal) * 0.005) +
            toNum(formDataRefinance.spouseCreditCard1) +
            toNum(formDataRefinance.spouseCreditCard2) +
            toNum(formDataRefinance.spouseCreditCard3) +
            toNum(formDataRefinance.spouseCreditCard4) +
            toNum(formDataRefinance.spouseStoreCards) +
            toNum(formDataRefinance.spouseFurnitureLoans) +
            toNum(formDataRefinance.spouseApplianceLoans) +
            toNum(formDataRefinance.spouseOtherDebts)
          ).toFixed(2);
          let totalLoanRequested = toNum(
            formDataRefinance.mortgageBalance || 0
          );

          if (Array.isArray(consolidatedFields)) {
            consolidatedFields.forEach((fieldName) => {
              if (fieldName.toLowerCase() !== "mortgagebalance") {
                totalLoanRequested += toNum(formDataRefinance[fieldName] || 0);
              }
            });
          }

          // ✅ Updated formData to pass to PreQualRefi:
          const updatedFormData = {
            ...formDataRefinance,
            totalDebts: parseFloat(totalDebts),
            transactionType: "Refinance",
            totalLoanRequested: totalLoanRequested,
            consolidatedFields: consolidatedFields,
          };

          const result = calculateRefinancePreQual(updatedFormData);
          const fhaAmount = result.pricing?.FHA;

          if (!fhaAmount || fhaAmount <= 0) {
            alert(
              "Para continuar con su precalificación, por favor comuníquese directamente con un empleado de Home Mortgage Bankers. La información de contacto estará disponible en su panel (dashboard)."
            );
            navigate("/es/dashboard");
            return;
          }

          const resultForPDF = {
            eligibleProducts: ["FHA"],
            pricing: { FHA: fhaAmount },
            grantEligible: false,
          };

          const lettersMap = await generatePreQualRefipdf(
            resultForPDF,
            updatedFormData
          );

          await uploadAndStorePreQualPDFs(docId, lettersMap, "Refinance");

          await updateDoc(docRef.current, {
            formDataRefinance: {
              ...updatedFormData,
              completed: true,
            },
            completed: true,
            currentStepR: 16,
            transactionType: "Refinance",
            updatedAt: new Date(),
          });

          navigate("/es/dashboard");
        };

        return (
          <QuestionCard title="Finalizar y Enviar" onBack={handleBack}>
            <p style={{ marginBottom: "20px", fontSize: "16px" }}>
              Haga clic abajo para enviar su informacion y realizar su
              Precalificación de refinanciamiento.
            </p>

            <button
              onClick={handleRefinanceSubmit}
              style={{
                ...buttonStyle,
                backgroundColor: "#28a745",
                width: "100%",
              }}
            >
              Enviar
            </button>
          </QuestionCard>
        );

      default:
        return (
          <div style={{ padding: "40px", textAlign: "center" }}>
            Step {step} not implemented.
          </div>
        );
    }
  };

  return (
    <>
      <style>
        {`
  @media (max-width: 700px) {
    .rfq-container {
      padding: 6vw 2vw !important;
      margin: 6vw auto !important;
      border-radius: 10px !important;
      max-width: 98vw !important;
      box-sizing: border-box !important;
    }
    .rfq-row,
    .rfq-btns,
    .rfq-checks {
      flex-direction: column !important;
      gap: 0 !important;
      align-items: stretch !important;
      width: 100% !important;
    }
    .rfq-input {
      width: 100% !important;
      min-width: 0 !important;
      margin-bottom: 14px !important;
      font-size: 1.05em !important;
      box-sizing: border-box !important;
    }
    .rfq-check-label {
      font-size: 15px !important;
      min-width: 0 !important;
      white-space: normal !important;
      width: 100% !important;
      display: block !important;
    }
    .rfq-check-col {
      width: 100% !important;
      min-width: 0 !important;
      margin-right: 0 !important;
      display: block !important;
    }
    .debt-actions {
      flex-direction: row !important;
      justify-content: space-between !important;
      width: 100% !important;
      margin-top: 10px;
      gap: 18px !important;
    }
    .debt-actions > div {
      min-width: 0 !important;
    }
  }
`}
      </style>
      <div style={{ position: "relative" }}>
        <div style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
          <LanguageToggle />
        </div>
        {renderStep()}
      </div>
    </>
  );
};

export default RefinanceQuestions;
